import * as React from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { formatIsoForDisplay, parseDateInput } from "@/components/ui/date-input-parser"

export interface DateInputProps {
  /** ISO 8601 date string (YYYY-MM-DD) or undefined when empty. */
  value: string | undefined
  /** Emits ISO 8601 (YYYY-MM-DD) on successful parse, or undefined when cleared. */
  onChange: (iso: string | undefined) => void
  id?: string
  name?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  className?: string
  "aria-label"?: string
  "aria-describedby"?: string
}

const DEFAULT_PLACEHOLDER = "MM/DD/YYYY"
const PARSE_ERROR_MESSAGE = "Invalid date. Use MM/DD/YYYY or MM/DD/YY."

export function DateInput({
  value,
  onChange,
  id,
  name,
  placeholder = DEFAULT_PLACEHOLDER,
  disabled,
  required,
  className,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}: DateInputProps) {
  const reactId = React.useId()
  const inputId = id ?? reactId
  const errorId = `${inputId}-error`

  // Canonical display string derived from the controlled ISO value.
  const canonicalDisplay = React.useMemo(
    () => (value ? formatIsoForDisplay(value) : ""),
    [value],
  )

  // Local text state so the user can type freely (e.g. "6/1/25") without
  // every keystroke triggering parsing or onChange.
  const [display, setDisplay] = React.useState<string>(canonicalDisplay)
  const [error, setError] = React.useState<string | null>(null)
  const lastCanonicalRef = React.useRef(canonicalDisplay)

  // Sync local display when the controlled value changes from outside.
  React.useEffect(() => {
    if (canonicalDisplay !== lastCanonicalRef.current) {
      lastCanonicalRef.current = canonicalDisplay
      setDisplay(canonicalDisplay)
      setError(null)
    }
  }, [canonicalDisplay])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplay(e.target.value)
    if (error) setError(null)
  }

  const handleBlur = () => {
    const trimmed = display.trim()

    if (trimmed === "") {
      setError(null)
      setDisplay("")
      if (value !== undefined) {
        lastCanonicalRef.current = ""
        onChange(undefined)
      }
      return
    }

    const parsed = parseDateInput(trimmed)
    if (parsed === null) {
      setError(PARSE_ERROR_MESSAGE)
      return
    }

    setError(null)
    const nextCanonical = formatIsoForDisplay(parsed)
    lastCanonicalRef.current = nextCanonical
    setDisplay(nextCanonical)
    if (parsed !== value) {
      onChange(parsed)
    }
  }

  const describedBy =
    [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(" ") || undefined

  return (
    <div className={cn("w-full", className)}>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={inputId}
        name={name}
        placeholder={placeholder}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(error && "border-destructive focus-visible:ring-destructive")}
      />
      {error ? (
        <p id={errorId} role="alert" className="mt-1 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
